/**
 * Thin fetch wrapper for the Identity Fabric console.
 * - Always sends credentials so the HttpOnly `identity_session` cookie rides along.
 * - Parses ServiceError JSON bodies on 4xx and surfaces them via ApiError.
 * - Distinguishes 401 (no/expired session) and 403 (logged in but missing
 *   `platform_admin`) so the auth probe can route accordingly.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  readonly kind: 'unauthorized' | 'forbidden' | 'client' | 'server' | 'network';

  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
    if (status === 401) this.kind = 'unauthorized';
    else if (status === 403) this.kind = 'forbidden';
    else if (status >= 500) this.kind = 'server';
    else if (status >= 400) this.kind = 'client';
    else this.kind = 'network';
  }

  get isUnauthorized(): boolean {
    return this.kind === 'unauthorized';
  }
  get isForbidden(): boolean {
    return this.kind === 'forbidden';
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** Either an object (sent as JSON) or a raw string (sent verbatim). */
  body?: unknown;
  /** Override `Content-Type` (defaults to JSON for object bodies, text for strings). */
  contentType?: string;
  /** Extra headers merged on top of the defaults. */
  headers?: Record<string, string>;
  /** Optional AbortSignal for cancellation. */
  signal?: AbortSignal;
}

async function readErrorBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function extractMessage(status: number, body: unknown): string {
  if (body && typeof body === 'object' && body !== null && 'error' in body) {
    const err = (body as { error?: unknown }).error;
    if (typeof err === 'string') return err;
  }
  if (typeof body === 'string' && body.length > 0) return body;
  return `HTTP ${status}`;
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, contentType, headers = {}, signal } = options;

  const init: RequestInit = {
    method,
    credentials: 'include',
    headers: { Accept: 'application/json', ...headers },
    signal,
  };

  if (body !== undefined && body !== null) {
    if (typeof body === 'string') {
      init.body = body;
      (init.headers as Record<string, string>)['Content-Type'] =
        contentType ?? 'text/plain; charset=utf-8';
    } else {
      init.body = JSON.stringify(body);
      (init.headers as Record<string, string>)['Content-Type'] =
        contentType ?? 'application/json';
    }
  }

  let response: Response;
  try {
    response = await fetch(path, init);
  } catch (err) {
    throw new ApiError(
      `network error: ${err instanceof Error ? err.message : String(err)}`,
      0,
      null,
    );
  }

  if (!response.ok) {
    const errBody = await readErrorBody(response);
    throw new ApiError(extractMessage(response.status, errBody), response.status, errBody);
  }

  // 204 No Content
  if (response.status === 204) return undefined as T;

  const text = await response.text();
  if (!text) return undefined as T;

  try {
    return JSON.parse(text) as T;
  } catch {
    // Endpoint returned non-JSON; surface as-is.
    return text as unknown as T;
  }
}
